<template>
  <div class="_wrapper" :class="{ 'is-shop': displayMode === 'shop' }">
    <div id="home" class="flex-full" v-if="currentPartner && displayMode === 'food'">
      <head-image />
      <custom-search />
      <div class="menu-bottom">
        <main-menu />
      </div>
      <div class="container pt15">
        <button-back-home-category :partner="currentPartner"/>
        <promo-item v-if="currentPartner.promotions.length"/>
        <recently-bought-from-partner />
        <most-ordered-from-partner />
        <category-grid />
        <breadcrumbs v-if="activeLocation" class="pt20" :routes="breadcrumbRoutes" :active-route="currentPartner.title" />
      </div>
    </div>
    <div class="block mt20" v-else-if="currentPartner && displayMode === 'shop'">
      <promo-item />
      <recently-bought-from-partner />
      <most-ordered-from-partner />
      <shop-category-grid :counter="preloaderItems" />
      <shop-category-swipers />
      <breadcrumbs v-if="activeLocation" class="pt20" :routes="breadcrumbRoutes" :active-route="currentPartner.title" />
    </div>
  </div>
</template>

<script async>
import config from 'config'
import HeadImage from 'theme/components/core/blocks/MainSlider/HeadImage'
import CustomSearch from 'theme/components/core/blocks/SearchPanel/CustomSearch'
import MainMenu from 'theme/components/core/blocks/Header/MainMenu.vue'
import { mapGetters, mapActions } from 'vuex'
import ButtonBackHomeCategory from '../components/theme/blocks/Partners/ButtonBackHomeCategory';
import Breadcrumbs from 'theme/components/core/Breadcrumbs.vue'
import { Logger } from '@vue-storefront/core/lib/logger'
import { curatedProducts } from '@vue-storefront/core/lib/catalogSearch'
import { s3Path, isPartnerHidden } from '@vue-storefront/core/helpers'
import { localizedRoute, currentStoreView } from '@vue-storefront/core/lib/multistore'
import i18n from '@vue-storefront/i18n'
const PromoItem = () => import('theme/components/core/blocks/Shop/Product/PromoItem')
const CategoryGrid = () => import('theme/components/theme/blocks/Home/CategoryGrid')
const ShopCategoryGrid = () => import('theme/components/core/blocks/Shop/Product/CategoryGrid')
const ShopCategorySwipers = () => import('theme/components/core/blocks/Shop/Product/CategorySwipers')
const RecentlyBoughtFromPartner = () => import('theme/components/theme/blocks/Partners/RecentlyBoughtFromPartner')
const MostOrderedFromPartner = () => import('theme/components/theme/blocks/Partners/MostOrderedFromPartner')

const preloadCategoryProducts = async (store) => {
  let categories = store.getters['category/getCategories']
  if (!categories.length) {
    return
  }
  let limiter = categories.length > 2 ? 2 : categories.length
  let size = 12
  let partner = store.getters['partners/getCurrentPartner']
  if (partner && partner.display_mode === 'food') {
    limiter = categories.length
    size = 1000
  }
  let categoryIdsToLoad = []
  for (let i = 0; i < limiter; i++) {
    categoryIdsToLoad.push(categories[i].entity_id)
  }
  let sorting = (partner['product_sort'] === 'sort_range') ? 'category.position:asc' : 'sum_score:desc';
  await store.dispatch('product/list', {
    semanticFetch: () => curatedProducts({ categoryIds: categoryIdsToLoad, start: 0, size, sort: sorting }),
    updateState: true
  })
}

export default {
  components: {
    MainMenu,
    CustomSearch,
    HeadImage,
    CategoryGrid,
    ShopCategoryGrid,
    ShopCategorySwipers,
    PromoItem,
    ButtonBackHomeCategory,
    RecentlyBoughtFromPartner,
    MostOrderedFromPartner,
    Breadcrumbs
  },
  data () {
    return {
      preloaderItems: 9,
      scrollFixTop: false
    }
  },
  async asyncData ({ store, route, context }) { // this is for SSR purposes to prefetch data
    await store.dispatch('partners/ensureCurrentPartner', { code: route.params.store, locationCode: route.params.locationCode })
    const current = store.getters['partners/getCurrentPartner']
    if (!current || current.code !== route.params.store) {
      Logger.error('Cannot enter asyncData in Partner View: partner not loaded, route: ' + JSON.stringify(route.params))()
      return
    }
    if (isPartnerHidden(store.getters['partners/getCurrentPartner'].entity_id)) {
      // Same magic-substring convention product/actions.ts::fetch() uses - both
      // server-entry.ts and client-entry.ts key off this exact phrase to route the
      // rejection to a real 404 (SSR) / redirect-home (client nav) instead of a
      // generic error page.
      throw new Error(`Partner query returned empty result for hidden partner_id = ${store.getters['partners/getCurrentPartner'].entity_id}`)
    }
    Logger.info('Entering asyncData in Partner View')()
    try {
      if (context) context.output.cacheTags.add(`partner`)
      if (store.getters['partners/getCurrentPartner']) {
        let partner = store.getters['partners/getCurrentPartner']
        // Only backs the optional "Back to catalog" link, so it never fails the render.
        let partnerCategoriesPromise = store.dispatch('partner_categories/ensureCategories', { locationCode: partner.location_code }).catch(error => {
          Logger.error(error, 'sync')()
        })
        await Promise.all([
          store.dispatch('category/list', {
            parent_id: partner.root_category_id,
            onlyNotEmpty: true,
            level: 2
          }),
          partnerCategoriesPromise
        ])
      }
      await preloadCategoryProducts(store)
    } catch (err) {
      Logger.error(err)()
      throw err
    }
  },
  mounted () {
    if (!this.isLoggedIn && localStorage.getItem('redirect')) this.$bus.$emit('modal-show', 'modal-signup')
    window.addEventListener('scroll', this.handleScroll)
    this.isPartnerPage();
  },
  computed: {
    ...mapGetters({
      isLoggedIn: 'user/isLoggedIn',
      currentPartnerCode: 'partners/getCurrentPartnerCode',
      currentPartner: 'partners/getCurrentPartner',
      displayMode: 'partners/getCurrentPartnerDisplayMode',
      metaInformation: 'configs/getMetaInformation',
      activeLocation: 'locations/getActiveLocation',
      getPartnerCategoryById: 'partner_categories/getCategoryById'
    }),
    // No "primary category" flag exists on partners_categories_connection - a
    // partner can belong to several categories (e.g. myasorub), so this just
    // takes the first, matching backend array order.
    partnerCategory () {
      if (!this.currentPartner || !this.currentPartner.categories || !this.currentPartner.categories.length) return null
      return this.getPartnerCategoryById(this.currentPartner.categories[0])
    },
    breadcrumbRoutes () {
      const routes = [
        { name: i18n.t('All Stores'), route_link: localizedRoute('/', currentStoreView().storeCode) },
        { name: this.activeLocation.name, route_link: `/${this.activeLocation.code}` }
      ]
      if (this.partnerCategory) {
        routes.push({ name: this.partnerCategory.title, route_link: `/${this.activeLocation.code}/${this.partnerCategory.code}` })
      }
      return routes
    },
    scrollTag () {
      if (this.$refs.tagCategory !== undefined) return this.$refs.tagCategory.offsetTop + this.$refs.tagCategory.offsetHeight
    },
    ogMetaType () {
      if (this.metaInformation.find(meta => meta.key === 'og_type')) return this.metaInformation.find(meta => meta.key === 'og_type').value
      return ''
    },
    ogMetaTitle () {
      if (this.metaInformation.find(meta => meta.key === 'og_title')) return this.metaInformation.find(meta => meta.key === 'og_title').value
      return ''
    },
    ogMetaDescription () {
      if (this.metaInformation.find(meta => meta.key === 'og_description')) return this.metaInformation.find(meta => meta.key === 'og_description').value
      return ''
    },
    ogMetaImage () {
      if (this.metaInformation.find(meta => meta.key === 'og_image')) return this.metaInformation.find(meta => meta.key === 'og_image').value
      return this.currentPartner.logo_path ? s3Path(this.currentPartner.logo_path, config.images.partner_logo.width, config.images.partner_logo.height) : ''
    },
    getHostPath () {
      return config.canonical ? config.canonical.url : ''
    }
  },
  watch: {
    isLoggedIn () {
      const redirectObj = localStorage.getItem('redirect')
      if (redirectObj) this.$router.push(redirectObj)
      localStorage.removeItem('redirect')
    },
    '$route.path' () {
      this.searchText = ''
    }
  },
  methods: {
    ...mapActions({
      isPartnerPage: 'ui/togglePartnerPage',
      resetCategories: 'category/resetList'
    }),
    handleScroll () {
      this.scrollFixTop = window.scrollY > this.scrollTag
    }
  },
  metaInfo () {
    let text = ''
    if (this.currentPartner !== undefined) {
      if (this.currentPartner.hasOwnProperty('meta_title') && this.currentPartner.meta_title) {
        text = this.currentPartner.meta_title
      } else {
        text = this.currentPartner.display_mode === 'food'
          ? `Доставка до 60 хвилин з ${this.currentPartner.title}  - Зручне замовлення онлайн.`
          : `${this.currentPartner.about} - Доставка якнайшвидше або у вибраний час`
      }
      let currentParentMeta = [
        {
          vmid: 'description',
          name: 'description',
          content: this.currentPartner.meta_description ? this.currentPartner.meta_description
            : 'Привеземо Вам усе що забажаєте. У нас в каталозі Ви можете переглянути та замовити страви та товари з найкращих закладів харчування, супермаркетів та гіпермаркетів міста'
        },
        {
          vmid: 'keywords',
          name: 'keywords',
          content: this.currentPartner.meta_keywords || this.currentPartner.meta_keywords
        },
        {
          vmid: 'og:type',
          property: 'og:type',
          content: this.currentPartner.og_meta_type ? this.currentPartner.og_meta_type : this.ogMetaType
        },
        {
          vmid: 'og:title',
          property: 'og:title',
          content: this.currentPartner.og_meta_title ? this.currentPartner.og_meta_title : this.ogMetaTitle
        },
        {
          vmid: 'og:description',
          property: 'og:description',
          content: this.currentPartner.og_meta_description ? this.currentPartner.og_meta_description : this.ogMetaDescription
        },
        {
          vmid: 'og:image',
          property: 'og:image',
          content: this.currentPartner.og_meta_image ? this.currentPartner.og_meta_image : this.ogMetaImage
        },
        {
          vmid: 'og:url',
          property: 'og:url',
          content: `${this.getHostPath}/${this.currentPartner.location_code}/${this.currentPartner.code}`
        }
      ]
      return {
        title: text,
        meta: currentParentMeta,
        link: [
          { rel: 'canonical', href: `${this.getHostPath}/${this.currentPartner.location_code}/${this.currentPartner.code}` }
        ]
      }
    }
  },
  async beforeRouteLeave (to, from, next) {
    await this.resetCategories()
    next()
  },
  destroyed () {
    this.isPartnerPage()
  }
}
</script>

<style lang="scss" scoped>
#parent{
  width: 100%;
}

._wrapper {
  &.is-shop {
    @media only screen and (max-width: 576px) {
      width: calc(100% - 30px);
      padding: 0 15px;
    }
  }
}

.block {
  width: 850px;
  //min-height: 71vh;

  @media only screen and (max-width: 1200px) {
    width: 654px;
  }

  @media only screen and (max-width: 991px) {
    width: 722px;
  }

  @media only screen and (max-width: 768px) {
    width: 530px;
  }

  @media only screen and (max-width: 576px) {
    width: 100%;
  }
}

.fixTagsCategory{
  width: 100%;
  position: fixed;
  top: 0;
  z-index: 1;
  left: 0;
  background: #ffffff;
}
.material-icons.close{
  font-size: 21px;
  position: absolute;
  right: 0;
  top: -5px;
  color: #000;
  border-radius: 50%;
  background-color: #fff;
}
#home{
  position: relative;
  top: -54px;
  margin-bottom: -54px;
}
.homeShop{
  top: 0 !important;
  margin-bottom: 0 !important;
}
.new-collection {
  @media (max-width: 768px) {
    padding-top: 0;
  }
}
.menu-bottom {
  display: block;
  margin-top: -50px;
}
.tags{
  white-space: nowrap;
  overflow-y: hidden;
  &.current{
    .tag-wrap{
      display: none;
      &.active{
        display: inline-flex;
      }
    }
  }
}
.tag-wrap {
  height: 35px;
  border-radius: 35px;
  border: 1px solid #ebebeb;
  background-color: #ffffff;
  padding: 0 20px;
  font-size: 14px;
  font-family: "TTNorms Medium", sans-serif;
  &.active {
    background-color: #11bb2e;
    color: #fff;
    fill: #fff;
  }
}
.parent-bred span:nth-last-child(2) .slash{
  display: none;
}
.back-in-shop {
  margin-left: -8px;
}
</style>
